import {component} from '@reduct/component';
import propTypes from '@reduct/nitpick';
import $ from 'jquery';
import debounce from 'lodash.debounce';
import isNumber from 'lodash.isnumber';
import imagesLoaded from 'imagesloaded';

const $window = $(window);

const classSet = {
	slide: {
		prev: 'contentSlider__slidesWrapper__slide--slideLeft',
		current: 'contentSlider__slidesWrapper__slide--slideCenter',
		next: 'contentSlider__slidesWrapper__slide--slideRight',
		inView: 'contentSlider__slidesWrapper__slide--slideInView'
	}
};

export const eventNames = {
	slide: 'slide',
	slideNext: 'slideNext',
	slidePrev: 'slidePrev',
	findSlides: 'findSlides',
	adjustSliderHeight: 'adjustSliderHeight',
	initSliderIndexNav: 'initSliderIndexNav',
	slideFinished: 'slideFinished',
	refreshSliderIndexNav: 'refreshSliderIndexNav'
};

@component({
	indexWrapper: propTypes.object.isOptional,
	slidesWrapper: propTypes.object.isRequired,
	indexAnchors: propTypes.object.isOptional,
	nextAnchor: propTypes.object.isRequired,
	prevAnchor: propTypes.object.isRequired,
	fixedHeight: propTypes.bool.isOptional,
	slideSelector: propTypes.string.isOptional
})
export default class Slider {
	constructor() {
		const {
			indexWrapper,
			slidesWrapper,
			indexAnchors,
			nextAnchor,
			prevAnchor,
			fixedHeight,
			slideSelector
		} = this.props;

		const $indexWrapper = $(indexWrapper);
		const $slidesWrapper = $(slidesWrapper);
		const $indexAnchors = $(indexAnchors);
		const $nextAnchor = $(nextAnchor);
		const $prevAnchor = $(prevAnchor);

		// Check if a jquery element object is in place, or create one using the getElement method.
		this.$el = $(this.getElement());

		this.cssAnimationSlider = {
			type: 'slide',
			current: -1,
			isSliding: false,
			fixedHeight: fixedHeight || false,
			hasIndexNav: ($indexWrapper.length > 0),
			classSet: this.props.classSet || classSet.slide,
			slideSelector: slideSelector || '[data-slide]',

			$slidesWrapper,
			$indexWrapper,
			$indexAnchors,
			$nextAnchor,
			$prevAnchor
		};

		// Create a shortcut for the options.
		const options = this.cssAnimationSlider;

		// Events
		this.on(eventNames.slide, this.slide.bind(this));
		this.on(eventNames.slideNext, () => {
			const nextIndex = this.getNextIndex();

			this.slide({ index: nextIndex });
		});
		this.on(eventNames.slidePrev, () => {
			const prevIndex = this.getPrevIndex();

			this.slide({ index: prevIndex });
		});
		this.on(eventNames.findSlides, this.findSlides.bind(this));
		this.on(eventNames.adjustSliderHeight, this.adjustWrapperHeight.bind(this));
		this.on(eventNames.initSliderIndexNav, this.setupIndexNav.bind(this));
		this.on(eventNames.refreshSliderIndexNav, this.refreshIndexNav.bind(this));

		// Get the initial slides.
		this.trigger(eventNames.findSlides);

		// Adjust the min-height on window resize.
		const adjustSliderHeight = () => this.trigger(eventNames.adjustSliderHeight);
		const debouncedResize = debounce(adjustSliderHeight, 50);
		$window.on('resize orientationchange', debouncedResize);
		adjustSliderHeight();

		// Conditional features.
		// IndexNav
		if(options.hasIndexNav) {
			this.trigger(eventNames.initSliderIndexNav);
		}

		// AnchorNav
		if(options.$nextAnchor) {
			options.$nextAnchor.on('click', (e) => {
				e.preventDefault();
				e.stopImmediatePropagation();

				this.trigger(eventNames.slide, {
					index: this.cssAnimationSlider.current + 1
				});
			});
		}
		if(options.$prevAnchor) {
			options.$prevAnchor.on('click', (e) => {
				e.preventDefault();
				e.stopImmediatePropagation();

				this.trigger(eventNames.slide, {
					index: this.cssAnimationSlider.current - 1
				});
			});
		}

		// Slide to the first slide on render.
		this.trigger(eventNames.slide, {
			index: 0,
			forced: true
		});

		// Adjust the sliderHeight after all images have been loaded.
		imagesLoaded(slidesWrapper, adjustSliderHeight);
	}

	findSlides() {
		const options = this.cssAnimationSlider;

		options.$slides = options.$slidesWrapper.find(options.slideSelector);

		// If no data-slide el's are present, we assume the slide targets are cms generated, thus need a special setup.
		if(!options.$slides.length) {
			options.$slides = options.$slidesWrapper.children();
			options.$slides.addClass('contentSlider__slidesWrapper__slide');
		}

		this.cssAnimationSlider = options;
	}

	getSlides() {
		return this.cssAnimationSlider.$slides;
	}

	setupIndexNav() {
		const options = this.cssAnimationSlider;
		const $els = options.$indexAnchors;

		if(!$els.length) {
			options.$slides.each(() => {
				options.$indexWrapper.append('<li><a href=""></a></li>');
			});
			options.$indexAnchors = options.$indexWrapper.children();
		}

		this.trigger(eventNames.refreshSliderIndexNav);

		options.$indexAnchors.each((index, item) => {
			const $item = $(item);

			$item.on('click', (e) => {
				e.preventDefault();
				e.stopImmediatePropagation();

				this.trigger(eventNames.slide, {
					index: $item.data('nav') || index,
					forced: false
				});
			});
		});

		this.cssAnimationSlider = options;
	}

	refreshIndexNav() {
		const options = this.cssAnimationSlider;

		options.$indexAnchors.removeClass('active');
		options.$indexAnchors.eq(options.current).addClass('active');

		this.cssAnimationSlider = options;
	}

	/**
	 * @description Adjusts the height of the slidesWrapper
	 * @param args {Number} This param, represents an optional number which will be used as the height.
	 */
	adjustWrapperHeight(args) {
		const $currentSlide = this.getCurrentSlide();
		let height;

		// Check if the passed arg is a number
		if(isNumber(args)) {
			height = args;
		} else {
			height = $currentSlide.height() + parseInt($currentSlide.css('padding-top')) + parseInt($currentSlide.css('padding-bottom'))  + parseInt($currentSlide.css('top'));
		}

		// If the fixedHeight option was set, calculate the greatest height of the slides.
		// Additionally we cover the dataset option for the 'Art of Travel'-Service.
		if(this.cssAnimationSlider.fixedHeight) {
			const maxHeight = 0;
			this.cssAnimationSlider.$slides.css({ height: 'auto' });

			this.cssAnimationSlider.$slides.each(function(el) {
				var $el = $(this);

				if($el.height() > maxHeight) {
					maxHeight = $el.height();
				}
			});

			height = maxHeight;
		}

		this.cssAnimationSlider.$slidesWrapper.css({ height });
	}

	getCurrentSlide() {
		return this.cssAnimationSlider.$slides.eq(this.cssAnimationSlider.current);
	}

	getNextIndex() {
		return this.cssAnimationSlider.current + 1;
	}

	getPrevIndex() {
		return this.cssAnimationSlider.current - 1;
	}

	validateIndex(index) {
		const maxIndex = this.cssAnimationSlider.$slides.length - 1;

		// Fix the index if it reaches the max / min index val.
		index = (index < 0) ? maxIndex : index;
		index = (index > maxIndex) ? 0 : index;

		return index;
	}

	slide(opts = {}) {
		const { index, forced } = opts;
		const $prev = this.getCurrentSlide();
		const options = this.cssAnimationSlider;

		// Prevent duplicate slide init's.
		if(this.cssAnimationSlider.isSliding && !forced) {
			return;
		}

		// Prevent sliding to the current slide, since the transitionend callback won't fire in such case.
		if(index === options.current && !forced) {
			return;
		}

		// Prevent double taps on the slide controls.
		options.isSliding = true;

		// Set the currentSlide Index
		this.cssAnimationSlider.current = this.validateIndex(index);

		this.stopMediaElements($prev);
		this.startAnimation($prev);
	}

	stopMediaElements($prevSlide) {
		$prevSlide.find('video').each(
			(index, videoElem) => $(videoElem).trigger('videoElement__pause')
		);
	}

	startAnimation($prev) {
		const { classSet } = this.cssAnimationSlider;
		const $target = this.getCurrentSlide();

		// Before the animation starts, reset some classes.
		this.onBeforeAnimation($target);

		// Start the animation/transition process.
		$prev.addClass(classSet.inView);
		this.trigger(eventNames.adjustSliderHeight);

		$target.addClass(classSet.current + ' ' + classSet.inView);

		if(window.Modernizr.cssanimations || $target.css('transition-duration') === '0s') {
			$target.on('transitionend', () => {
				this.onAnimationFinished($prev);
			});
		} else {
			this.onAnimationFinished($prev);
		}
	}

	onBeforeAnimation($target) {
		const { classSet, index, $slides } = this.cssAnimationSlider;
		const parentClass = $target.data('parentclass');

		// Remove all classes.
		$slides.removeClass(classSet.prev + ' ' + classSet.current + ' ' + classSet.next);

		// Move the prev and next elements in the correct initial position for sliding.
		$target.prevAll().addClass(classSet.prev);
		$target.nextAll().addClass(classSet.next);

		// If the index is the first/last, the initial position of those elements need to be corrected.
		if(index === 0) {
			$slides.last().removeClass(classSet.prev + ' ' + classSet.next).addClass(classSet.next);
		}

		if(index ===  $slides.length -1) {
			$slides.first().removeClass(classSet.prev + ' ' + classSet.next).addClass(classSet.prev);
		}

		// If the slide has an data-attribute of 'parentClass', set the val on the constructors el as the slide switches.
		this.$el.removeClass(this.state.additionalSlideClass);

		if(parentClass) {
			this.setState({additionalSlideClass: parentClass});
			this.$el.addClass(parentClass);
		}
	}

	onAnimationFinished($prev) {
		const { classSet, hasIndexNav } = this.cssAnimationSlider;

		$prev.removeClass(classSet.inView);

		// Refresh the indexNav state.
		if(hasIndexNav) {
			this.trigger(eventNames.refreshSliderIndexNav);
		}

		this.trigger(eventNames.slideFinished);

		// Prevent double taps on the slide controls.
		this.cssAnimationSlider.isSliding = false;
	}
};
